/**
 * Normalizes account type payload for withdrawal charge rules (Laravel API).
 * Keeps existing string "0"/"1" pattern for apply_withdrawal_charges unless caller overrides.
 */

export function normalizeAccountTypeWithdrawalPayload(form) {
	if (!form || typeof form !== "object") {
		return form;
	}
	const out = { ...form };
	const applyRaw = out.apply_withdrawal_charges;
	const apply =
		applyRaw === "1" ||
		applyRaw === 1 ||
		applyRaw === true ||
		applyRaw === "true";

	if (!apply) {
		out.apply_withdrawal_charges = "0";
		out.withdrawal_charges_amount = "";
		out.charges_coa = null;
		out.withdrawal_charge_mode = "flat";
		out.withdrawal_charge_frequency = "every_transaction";
		out.withdrawal_charge_quota_period = "";
		out.withdrawal_charge_quota_free_withdrawals = "";
		return out;
	}

	out.apply_withdrawal_charges = "1";

	if (!out.withdrawal_charge_mode) {
		out.withdrawal_charge_mode = "flat";
	}
	if (!out.withdrawal_charge_frequency) {
		out.withdrawal_charge_frequency = "every_transaction";
	}

	if (out.withdrawal_charge_frequency === "every_transaction") {
		out.withdrawal_charge_quota_period = "";
		out.withdrawal_charge_quota_free_withdrawals = "";
	}

	if (out.charges_coa && typeof out.charges_coa === "object" && out.charges_coa.id != null) {
		out.charges_coa = { id: out.charges_coa.id };
	}

	if (out.withdrawal_charges_amount !== "" && out.withdrawal_charges_amount != null) {
		const n = Number(out.withdrawal_charges_amount);
		if (!Number.isNaN(n)) {
			out.withdrawal_charges_amount = n;
		}
	}

	if (
		out.withdrawal_charge_frequency === "after_quota" &&
		out.withdrawal_charge_quota_free_withdrawals !== "" &&
		out.withdrawal_charge_quota_free_withdrawals != null
	) {
		const q = parseInt(out.withdrawal_charge_quota_free_withdrawals, 10);
		if (!Number.isNaN(q)) {
			out.withdrawal_charge_quota_free_withdrawals = q;
		}
	}

	return out;
}

/**
 * Client-side checks mirroring common Laravel messages; server errors still win via API response.
 */
export function validateWithdrawalChargesClient(form) {
	const errs = {};
	const applyRaw = form?.apply_withdrawal_charges;
	const apply =
		applyRaw === "1" ||
		applyRaw === 1 ||
		applyRaw === true ||
		applyRaw === "true";
	if (!apply) {
		return { ok: true, errors: errs };
	}

	const mode = form.withdrawal_charge_mode || "flat";
	const amt = form.withdrawal_charges_amount;
	const freq = form.withdrawal_charge_frequency || "every_transaction";

	if (amt === "" || amt === null || amt === undefined) {
		errs.withdrawal_charges_amount = ["The withdrawal charges amount field is required."];
	} else if (mode === "percentage_rate") {
		const n = Number(amt);
		if (!Number.isFinite(n) || n > 100) {
			errs.withdrawal_charges_amount = [
				"The withdrawal charges amount must not be greater than 100.",
			];
		}
	}

	const coa = form.charges_coa;
	const hasCoa =
		coa &&
		(typeof coa === "object"
			? coa.id != null && String(coa.id) !== ""
			: String(coa) !== "");
	if (!hasCoa) {
		errs.charges_coa = ["The charges coa field is required."];
	}

	if (freq === "after_quota") {
		const p = form.withdrawal_charge_quota_period;
		if (!p || String(p).trim() === "") {
			errs.withdrawal_charge_quota_period = [
				"The withdrawal charge quota period field is required when charging after quota.",
			];
		}
		const free = form.withdrawal_charge_quota_free_withdrawals;
		if (free === "" || free === null || free === undefined) {
			errs.withdrawal_charge_quota_free_withdrawals = [
				"The withdrawal charge quota free withdrawals field is required when charging after quota.",
			];
		} else {
			const fi = Number(free);
			if (!Number.isFinite(fi) || fi < 0 || !Number.isInteger(fi)) {
				errs.withdrawal_charge_quota_free_withdrawals = [
					"The withdrawal charge quota free withdrawals must be a non-negative integer.",
				];
			}
		}
	}

	return {
		ok: Object.keys(errs).length === 0,
		errors: errs,
	};
}
